import { CircleCheck, Download, Monitor, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { type InstallState, useInstallPrompt } from '../../../sw/install-prompt';
import { Button } from '../../../ui/Button';
import { ListRow } from '../../../ui/ListRow';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { toast } from '../../../ui/Toast';

const STATE_TEXT: Record<InstallState, string> = {
  installed: 'Zainstalowana — otwierasz ją z ekranu głównego, powiadomienia przychodzą w tle.',
  available: 'Dodaj Fravity OS do ekranu głównego: pełny ekran, szybki start i powiadomienia push.',
  ios: 'Na iPhonie: w Safari stuknij „Udostępnij”, potem „Do ekranu początkowego”.',
  unavailable:
    'Zainstalujesz ją z Chrome na telefonie (menu ⋮ → „Zainstaluj aplikację”). Wymaga wersji produkcyjnej z HTTPS.',
};

/**
 * Instrukcje instalacji (Fala 10, DECISIONS „Android: najpierw PWA zamiast APK”): komputer i Android. Zainstalowana
 * aplikacja (okno bez paska adresu, `display: standalone`) ma to samo co karta przeglądarki: bąbel AI, ⌘J / Ctrl+J
 * i mikrofon (zgoda na mikrofon jak dla strony). Telefon wymaga adresu HTTPS — po wdrożeniu.
 */
const INSTALL_STEPS = [
  {
    icon: Monitor,
    title: 'Komputer (Chrome, Edge)',
    text: 'W pasku adresu kliknij ikonę „Zainstaluj” albo ⋮ → „Przesyłaj, zapisz i udostępnij” → „Zainstaluj stronę jako aplikację”. Fravity OS otworzy się w osobnym oknie; bąbel AI (Ctrl+J) i mikrofon działają tak samo jak w karcie.',
  },
  {
    icon: Smartphone,
    title: 'Telefon z Androidem (Chrome)',
    text: 'Otwórz Fravity OS w Chrome, stuknij ⋮ → „Zainstaluj aplikację” (albo „Dodaj do ekranu głównego”). Wymaga adresu HTTPS — działa po wdrożeniu na serwer. Przy pierwszym użyciu mikrofonu Chrome zapyta o zgodę.',
  },
] as const;

/**
 * Ustawienia → Powiadomienia: „Zainstaluj aplikację” (PWA zamiast APK, DECISIONS 2026-10-04). Bez projektu w Figmie —
 * wzorzec sekcji „To urządzenie”. Monit przeglądarki tylko na prośbę osoby; po instalacji push działa przy aplikacji
 * w tle, a link z powiadomienia otwiera rekord po zalogowaniu.
 */
export function InstallAppSection() {
  const { state, install } = useInstallPrompt();
  const [busy, setBusy] = useState(false);

  const onInstall = async () => {
    setBusy(true);
    try {
      const outcome = await install();
      if (outcome === 'accepted')
        toast.success('Zainstalowano Fravity OS', { description: 'Znajdziesz ją na ekranie głównym.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section className="mt-6" aria-labelledby="install-app">
      <SectionHeader tone="strong" title={<span id="install-app">Zainstaluj Fravity OS jako aplikację</span>} />
      <div className="border-t border-line">
        <ListRow
          height="l"
          leading={
            state === 'installed' ? (
              <CircleCheck size={18} strokeWidth={1.75} aria-hidden className="text-success" />
            ) : (
              <Smartphone size={18} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
            )
          }
          title="Fravity OS na ekranie głównym"
          description={<span className="whitespace-normal">{STATE_TEXT[state]}</span>}
          trailing={
            state === 'available' ? (
              <Button variant="secondary" size="s" icon={Download} loading={busy} onClick={() => void onInstall()}>
                Zainstaluj aplikację
              </Button>
            ) : undefined
          }
        />
        {INSTALL_STEPS.map((step) => (
          <ListRow
            key={step.title}
            height="l"
            leading={<step.icon size={18} strokeWidth={1.75} aria-hidden className="text-fg-muted" />}
            title={step.title}
            description={<span className="whitespace-normal">{step.text}</span>}
          />
        ))}
      </div>
    </Section>
  );
}
